// Cockpit revamp — status-transition matrix.
//
// Matches the design handoff README's STATUS_TRANSITIONS table verbatim,
// modulo backend naming: the backend's terminal state is `force_ended`
// (not `completed`); the design's "Force-end campaign" verb still maps to
// the `force_ended` target.
//
// Header buttons render *all* possible transitions for the lifecycle, but
// `disabled` reflects this matrix exactly: invalid actions get a tooltip
// explaining why ("Not allowed from status: <current>"). Never hand-roll
// per-status branches in components — read from STATUS_TRANSITIONS.
import type {
  CampaignStatus,
  CampaignTransitionTarget,
} from '../../../types/campaign';

export type TransitionKind =
  | 'launch'
  | 'pause'
  | 'resume'
  | 'close_enrolment'
  | 'force_end';

/** The legal next-state map. Keys are the *current* status; values map a
 * transition kind to the resulting status. A status whose entry is `{}`
 * is terminal — no further transitions allowed. */
export const STATUS_TRANSITIONS: Record<
  CampaignStatus,
  Partial<Record<TransitionKind, CampaignStatus>>
> = {
  draft: { launch: 'active' },
  active: {
    pause: 'paused',
    close_enrolment: 'enrolment_closed',
    force_end: 'force_ended',
  },
  paused: { resume: 'active', force_end: 'force_ended' },
  enrolment_closed: { force_end: 'force_ended' },
  // The remaining four are terminal-or-frozen — no operator transitions.
  completed: {},
  force_ended: {},
  archived: {},
};

/** Backend POST target for each transition kind. Backend rejects illegal
 * combinations with 409 — STATUS_TRANSITIONS gates the UI; the server
 * remains the source of truth. */
export const TRANSITION_TARGET: Record<TransitionKind, CampaignTransitionTarget> = {
  launch: 'active',
  pause: 'paused',
  resume: 'active',
  close_enrolment: 'enrolment_closed',
  force_end: 'force_ended',
};

export interface TransitionDef {
  kind: TransitionKind;
  label: string;
  verb: string;
  intent: 'primary' | 'secondary' | 'danger';
  note: string;
  /** Lucide icon constructor. Constructors aren't bundled here — components
   * import the icon themselves and read `iconName` to keep this file
   * import-free. */
  iconName: 'Rocket' | 'Pause' | 'Play' | 'OctagonAlert' | 'Square';
}

export const TRANSITIONS: TransitionDef[] = [
  {
    kind: 'launch',
    label: 'Launch',
    verb: 'Launch campaign',
    intent: 'primary',
    note:
      'Snapshot the audience, pin the sequence version, and start dispatching.',
    iconName: 'Rocket',
  },
  {
    kind: 'pause',
    label: 'Pause',
    verb: 'Pause campaign',
    intent: 'secondary',
    note: 'No new dispatches; in-flight calls finish.',
    iconName: 'Pause',
  },
  {
    kind: 'resume',
    label: 'Resume',
    verb: 'Resume campaign',
    intent: 'primary',
    note:
      'Resume dispatching from where each enrolment paused. Sequence version stays pinned to its launch value.',
    iconName: 'Play',
  },
  {
    kind: 'close_enrolment',
    label: 'Close enrolment',
    verb: 'Close enrolment',
    intent: 'secondary',
    note: 'Existing enrolments continue; no new contacts can be added.',
    iconName: 'OctagonAlert',
  },
  {
    kind: 'force_end',
    label: 'Force-end',
    verb: 'Force-end campaign',
    intent: 'danger',
    note:
      'All in-flight steps stop. Pending callbacks remain owned by the callback queue (toggle the modal switch to also cancel them).',
    iconName: 'Square',
  },
];

export function isTransitionAllowed(
  current: CampaignStatus,
  kind: TransitionKind,
): boolean {
  return Boolean(STATUS_TRANSITIONS[current]?.[kind]);
}

export function nextStatus(
  current: CampaignStatus,
  kind: TransitionKind,
): CampaignStatus | null {
  return STATUS_TRANSITIONS[current]?.[kind] ?? null;
}

/** Right-rail "valid from <status>" pill list. */
export function validTransitionsOf(
  current: CampaignStatus,
): Array<{ kind: TransitionKind; target: CampaignStatus }> {
  const entry = STATUS_TRANSITIONS[current] ?? {};
  return Object.entries(entry).map(([kind, target]) => ({
    kind: kind as TransitionKind,
    target: target as CampaignStatus,
  }));
}

/** Pre-launch (draft) is the only state the cockpit treats specially in
 * KPI strip + tab content. Centralised so components stop reading
 * `status === 'draft'` ad-hoc.  */
export function isPreLaunch(status: CampaignStatus): boolean {
  return status === 'draft';
}

